import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";
import WebsiteMediaInput from "./WebsiteMediaInput";

function asText(value) {
    if (value === null || value === undefined) return "";
    if (value instanceof Date) return value.toISOString().slice(0, 10);
    return String(value);
}

export default function WebsiteNotifications() {
    const { requestAPI, showToast } = useAppContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [courses, setCourses] = useState([]);
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/notifications",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load notices");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
        requestAPI({
            requestPath: "website/courses",
            requestMethod: "GET",
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setCourses(data);
            },
        });
    }, [loadRows, requestAPI]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Notices" />
            <TabHeader
                className="px-3 pt-3"
                title="Notices"
                highlights={["Each notice is a card on the Notifications page", "Open a notice to add extra photos and files"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: { is_published: true } })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} courses={courses} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const navigate = useNavigate();
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/notifications/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Notice removed", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
            },
        });
    };
    const active = row.is_published !== false && row.is_published !== 0;
    const meta = [row.category, row.course].filter(Boolean).join(" ? ");
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.title || "Untitled"}</span>
                {meta ? <span className={TEXT_SMALL}>{meta}</span> : null}
            </div>
            <i className="pi pi-folder-open cursor-pointer" title="Photos and files" onClick={() => navigate(`/manage-website/notifications/${row.id}`)} />
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, courses, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_published: true, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const courseOptions = [];
    const seen = new Set();
    (courses || []).forEach((course) => {
        const title = String(course.title || "").trim();
        if (!title || seen.has(title)) return;
        seen.add(title);
        courseOptions.push({ label: title, value: title });
    });
    if (record.course && !seen.has(record.course)) courseOptions.unshift({ label: record.course, value: record.course });
    const save = () => {
        if (!String(record.title || "").trim()) {
            showToast({ severity: "error", summary: "Name required", detail: "Enter the notice title", life: 2000 });
            return;
        }
        requestAPI({
            requestPath: "website/notifications",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: { ...record, published_at: asText(record.published_at) || null, is_new: record.is_new === true || record.is_new === 1 ? true : null },
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Notice saved", life: 1000 });
                    setDialog({ visible: false });
                } else showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add a notice" : "Edit notice"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="title">Notice title</label>
                    </FloatLabel>
                    <FieldNote text="The heading on the card and on the notice page." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="category" className="w-full" value={record?.category ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, category: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="category">Category</label>
                    </FloatLabel>
                    <FieldNote text="Example: Exams, Results, or Admissions. This becomes a filter on the Notifications page." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <Dropdown inputId="course" className="w-full" options={courseOptions} optionLabel="label" optionValue="value" value={record?.course || null} onChange={(event) => setRecord((prev) => ({ ...prev, course: event.value || "" }))} filter showClear disabled={saving} />
                        <label htmlFor="course">For course</label>
                    </FloatLabel>
                    <FieldNote text="Shown as For Course on the card. The list comes from the shared Courses page." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="description" className="w-full" rows={3} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                        <label htmlFor="description">Short line on the card</label>
                    </FloatLabel>
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputTextarea id="content" className="w-full" rows={6} autoResize value={record?.content || ""} onChange={(event) => setRecord((prev) => ({ ...prev, content: event.target.value }))} disabled={saving} />
                        <label htmlFor="content">Full notice</label>
                    </FloatLabel>
                    <FieldNote text="Shown on the notice page. Start a new line for a new paragraph." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="published_at" className="w-full" value={asText(record?.published_at)} onChange={(event) => setRecord((prev) => ({ ...prev, published_at: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="published_at">Date</label>
                    </FloatLabel>
                    <FieldNote text="Example: 2026-03-15. Home shows the 3 newest dates." />
                </div>
                <WebsiteMediaInput label="Cover photo" field="image" hint="Required size: 1280 ? 720 px (16:9). A larger photo is scaled to fit the card and the notice page. The whole photo stays visible." record={record} setRecord={setRecord} disabled={saving} />
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="image_alt" className="w-full" value={record?.image_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, image_alt: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="image_alt">Cover description</label>
                    </FloatLabel>
                    <FieldNote text="Read by screen readers. Describe the cover photo." />
                </div>
                <WebsiteMediaInput label="Video" type="video" field="video_url" hint="Optional. The video plays on the notice page." record={record} setRecord={setRecord} disabled={saving} />
                <WebsiteMediaInput label="Video cover" field="video_thumbnail" hint="Required size: 1280 ? 720 px (16:9). A larger picture is scaled to fit the video frame. The whole picture stays visible." record={record} setRecord={setRecord} disabled={saving} />
                <WebsiteMediaInput label="PDF" type="pdf" field="pdf_url" hint="Optional. Adds a PDF button on the card and an attachment on the notice page." record={record} setRecord={setRecord} disabled={saving} />
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="pdf_title" className="w-full" value={record?.pdf_title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, pdf_title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="pdf_title">PDF name</label>
                    </FloatLabel>
                    <FieldNote text="Example: Exam timetable. Leave empty to show PDF Document." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="pdf_size" className="w-full" value={record?.pdf_size ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, pdf_size: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="pdf_size">PDF size</label>
                    </FloatLabel>
                    <FieldNote text="Optional. Example: 240 KB." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="cta_text" className="w-full" value={record?.cta_text ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cta_text: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="cta_text">Button text</label>
                    </FloatLabel>
                    <FieldNote text="The card button. Leave empty to show View Details." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="cta_url" className="w-full" value={record?.cta_url ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cta_url: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="cta_url">Extra link</label>
                    </FloatLabel>
                    <FieldNote text="Optional link on the notice page, such as /courses. The card still opens this notice." />
                </div>
                <div>
                    <FloatLabel className="mt-5">
                        <InputText id="tags" className="w-full" value={record?.tags ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, tags: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                        <label htmlFor="tags">Search words</label>
                    </FloatLabel>
                    <FieldNote text="Extra words the search box can match. They are not shown on the card." />
                </div>
                <div className="flex align-items-center gap-2 mt-4">
                    <Checkbox inputId="is_new" checked={record?.is_new === true || record?.is_new === 1} onChange={(event) => setRecord((prev) => ({ ...prev, is_new: event.checked ? true : null }))} disabled={saving} />
                    <label htmlFor="is_new" className={TEXT_SMALL}>Always show the NEW badge</label>
                </div>
                <FieldNote text="Leave this off and the site shows NEW for 7 days after the date." />
                <div className="flex align-items-center gap-2 mt-4">
                    <Checkbox inputId="is_featured" checked={!!record?.is_featured} onChange={(event) => setRecord((prev) => ({ ...prev, is_featured: event.checked }))} disabled={saving} />
                    <label htmlFor="is_featured" className={TEXT_SMALL}>Show the FEATURED badge</label>
                </div>
                <div className="flex align-items-center gap-2 mt-4">
                    <Checkbox inputId="is_important" checked={!!record?.is_important} onChange={(event) => setRecord((prev) => ({ ...prev, is_important: event.checked }))} disabled={saving} />
                    <label htmlFor="is_important" className={TEXT_SMALL}>Show the IMPORTANT badge</label>
                </div>
                <div className="flex align-items-center gap-2 mt-4">
                    <Checkbox inputId="is_published" checked={record?.is_published !== false && record?.is_published !== 0} onChange={(event) => setRecord((prev) => ({ ...prev, is_published: event.checked }))} disabled={saving} />
                    <label htmlFor="is_published" className={TEXT_SMALL}>Show this notice on the website</label>
                </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
